import React, { useEffect, useState } from "react";
import { getDevicesLive } from "./printManager.device.api";
import { useOrg } from "../orgs/org-context";
import DeviceRow from "./DeviceRow";
import PairPrintManager from "./PairPrintManager";
import "./printManager.css";
import { apiFetch } from "../../api/api";
import { useAuth } from "../../auth/auth-context";
import MovePrintManager from './MovePrintManager';
import { toast } from 'react-toastify';
import { confirmDialog } from '../../components/EveryIdDialogHost';

const DevicesPage = () => {
  const { activeOrgId, memberships = [] } = useOrg();
  const { user } = useAuth();
  const member = memberships.find(m => String(m.orgId?._id || m.orgId) === String(activeOrgId));
  const canRelease = user?.globals?.includes('superadmin') || member?.roles?.some(r => ['org:admin','org:owner'].includes(r));
  const [transferMessage, setTransferMessage] = useState('');
  const [releasing, setReleasing] = useState(false);
  const [pairing, setPairing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moveDevice,setMoveDevice]=useState(null);
  const canMove=Array.isArray(user?.globals)&&user.globals.includes('superadmin');

  const loadDevices = async () => {
    try {
      setLoading(true);
      const data = await getDevicesLive();
      setDevices(data);
    } catch (err) {
      console.error("Failed to load Print Managers");
    } finally {
      setLoading(false);
    }
  };

  async function retire(device) {
    if (!await confirmDialog("Retire this Print Manager? Its installation loses access; history is preserved. To move PCs, use Replace instead.", { confirmLabel: 'Retire' })) return;
    try { await apiFetch("/api/printmanager/pairing/retire", {method:"POST", body:{deviceId:device.deviceId}}); await loadDevices(); }
    catch { toast.error("Unable to retire this Print Manager. Check organization permissions."); }
  }

  async function release(device) {
    if (!await confirmDialog(`Release ${device.deviceName || device.deviceId} for transfer? Active or executable work blocks release. History stays in this organization. The destination must pair this PC as a new Print Manager.`, { confirmLabel: 'Release' })) return;
    setReleasing(true); setTransferMessage('');
    try {
      await apiFetch('/api/printmanager/transfer/request', {method:'POST',body:{deviceId:device.deviceId}});

      setTransferMessage('Release requested. PM3 will verify local work before revoking access. Follow its local setup page for destination pairing.');
      await loadDevices();
    } catch { setTransferMessage('Release blocked or not acknowledged. Check active work, pending receipts and PM3 connectivity, then retry the same release. History is preserved.'); }
    finally { setReleasing(false); }
  }

  useEffect(() => {
    setPairing(false);setMoveDevice(null); setDevices([]); setTransferMessage(''); loadDevices();

    const interval = setInterval(loadDevices, 8000); // 🔥 auto-refresh
    return () => clearInterval(interval);
  }, [activeOrgId]);

  return (
    <div className="pm-page">
      <div className="pm-header">
        <h2>Print Managers</h2>
        <button onClick={()=>setPairing(true)}>Add Print Manager</button>
        <button onClick={loadDevices}>Refresh</button>
      </div>

      {pairing && <PairPrintManager key={activeOrgId} onClose={()=>setPairing(false)} onConnected={loadDevices} />}
      {transferMessage && <p role="status">{transferMessage}</p>}
      {canMove&&moveDevice&&<MovePrintManager key={activeOrgId} device={moveDevice} onClose={()=>setMoveDevice(null)} onRequested={()=>{setTransferMessage('Move requested. PM3 will release, claim the destination, and verify discovery before printing is enabled.');loadDevices();}}/>}
      <div className="pm-table">
        <div className="pm-row pm-header-row">
          <div>Device</div>
          <div>Org</div>
          <div>Status</div>
          <div>Last Seen</div>
          <div>Printers</div>
          <div>Queue</div>
          <div>Actions</div>
        </div>

        {loading && <div className="pm-loading">Loading...</div>}

        {!loading && devices.length === 0 && (
          <div className="pm-empty">No devices found</div>
        )}

        {devices.map((d) => (
          <DeviceRow key={d.deviceId} device={d} onRetire={retire} onRelease={canRelease && !releasing ? release : undefined} onMove={canMove ? setMoveDevice : undefined} />
        ))}
      </div>
    </div>
  );
};

export default DevicesPage;
